import React from 'react';
import Styled from 'styled-components';
import PropTypes from 'prop-types';

import Spinner from 'core/components/Spinner';
import { getColor } from 'core/utils/Styles';

/*
	Make sure that the root of each child component has a `label` prop
	which is used here to display the tab label on the tab.

	<LazyTabs
		tabs={[
			{ id: "tab1", label: "Tab 1" },
			{ id: "tab2", label: "Tab 2" },
		]}
		activeTab={"tab1"}
		onChange={tab => setActiveTab(tab)}
	/>
*/
const LazyTabs = ({ activeTab, className, tabs, loading, mobile, onChange }) => {
	if (!activeTab) {
		activeTab = tabs[0].id;
	}
	return (
		/*
			This allows the tab component to be styled as a Styled Component, ex:
			const SomeBetterTabs = Styled(Tabs)`
				// some custom styles go here
			`;
		*/
		<TabsList className={className}>
			{tabs.map(({ id, label, shortLabel }) => (
				<StyledTab
					active={activeTab === id}
					key={id}
					onClick={() => onChange(id)}
				>
					{mobile && shortLabel ? shortLabel : label}
					{loading && (activeTab === id) ? (
						<Spinner style={{ margin: "0 8px" }} />
					) : null}
				</StyledTab>
			))}
		</TabsList>
	);
};

LazyTabs.propTypes = {
	tabs: PropTypes.arrayOf(PropTypes.shape({
		id: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
		shortLabel: PropTypes.string,
	})).isRequired,
	activeTab: PropTypes.string,
	className: PropTypes.string,
	loading: PropTypes.bool,
	mobile: PropTypes.bool,
	onChange: PropTypes.func.isRequired,
};

/* Styled Components */
const TabsList = Styled.ol`
	background: ${getColor('porcelain')};
	box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.04);
	height: 44px;
	padding: 0 0 1px 0;
	margin: 0, 0, 40px, 0;
	list-style-type: none;
	display: flex;
	flex-direction: row;
`;

// exported for tests
export const StyledTab = Styled.li`
	border-bottom: 3px solid ${props => props.active ? getColor("primaryBrand") : getColor('porcelain')};
	color: ${ ({ active }) => active ? getColor("darkTeal") : getColor("gray9") };
	cursor: pointer;
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	line-height: 3em;
`;

export default LazyTabs;
